import React, { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import api, { formatError } from '../lib/api';
import { toast } from 'sonner';

export default function NewTaskDialog({ open, onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [dueDate, setDueDate] = useState('');
  const [assigneeIds, setAssigneeIds] = useState([]);
  const [isGeneral, setIsGeneral] = useState(false);
  const [users, setUsers] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      api.get('/users').then(({ data }) => setUsers(data)).catch(() => {});
      setTitle(''); setDescription(''); setPriority('medium');
      setDueDate(''); setAssigneeIds([]); setIsGeneral(false);
    }
  }, [open]);

  if (!open) return null;

  const toggleAssignee = (id) => {
    setAssigneeIds((prev) => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim()) return toast.error('Title is required');
    if (!isGeneral && assigneeIds.length === 0) return toast.error('Pick assignees or mark as general');
    setSaving(true);
    try {
      const { data } = await api.post('/tasks', {
        title, description, priority,
        assignee_ids: isGeneral ? [] : assigneeIds,
        due_date: dueDate || null,
      });
      toast.success('Task created');
      onCreated?.(data);
      onClose();
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white border border-zinc-900 shadow-brutal w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="new-task-dialog">
        <div className="flex items-center justify-between border-b border-zinc-200 p-5">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">CREATE</div>
            <div className="font-display text-2xl font-black">New Task</div>
          </div>
          <button onClick={onClose} data-testid="close-new-task"><X size={20} /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-2">Title</label>
            <input data-testid="task-title-input" value={title} onChange={(e) => setTitle(e.target.value)} className="w-full border border-zinc-300 focus:border-[#002FA7] outline-none px-3 py-2.5 bg-white" placeholder="e.g., Prepare quarterly report" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-2">Description</label>
            <textarea data-testid="task-desc-input" value={description} onChange={(e) => setDescription(e.target.value)} rows={4} className="w-full border border-zinc-300 focus:border-[#002FA7] outline-none px-3 py-2.5 bg-white" placeholder="Details / context the team needs..." />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-2">Priority</label>
              <select data-testid="task-priority-select" value={priority} onChange={(e) => setPriority(e.target.value)} className="w-full border border-zinc-300 focus:border-[#002FA7] outline-none px-3 py-2.5 bg-white">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-2">Due Date</label>
              <input type="date" data-testid="task-due-input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="w-full border border-zinc-300 focus:border-[#002FA7] outline-none px-3 py-2.5 bg-white" />
            </div>
          </div>
          <div>
            <label className="flex items-center gap-2 mb-3 cursor-pointer">
              <input type="checkbox" data-testid="task-general-checkbox" checked={isGeneral} onChange={(e) => setIsGeneral(e.target.checked)} className="accent-[#002FA7]" />
              <span className="text-xs font-bold uppercase tracking-[0.15em]">General task (visible to all users)</span>
            </label>
            {!isGeneral && (
              <div>
                <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-2">Assign To</label>
                <div className="border border-zinc-300 max-h-44 overflow-y-auto divide-y divide-zinc-200">
                  {users.length === 0 && <div className="p-3 text-sm text-zinc-500">No other users yet.</div>}
                  {users.map((u) => (
                    <label key={u.id} className="flex items-center gap-3 px-3 py-2 hover:bg-zinc-50 cursor-pointer">
                      <input type="checkbox" data-testid={`assignee-${u.id}`} checked={assigneeIds.includes(u.id)} onChange={() => toggleAssignee(u.id)} className="accent-[#002FA7]" />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{u.name}</div>
                        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-500">{u.role} · {u.email}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-200">
            <button type="button" onClick={onClose} className="border border-zinc-900 px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] hover:bg-zinc-900 hover:text-white transition-colors" data-testid="cancel-task-btn">Cancel</button>
            <button type="submit" disabled={saving} className="bg-[#002FA7] text-white px-5 py-2.5 text-xs font-bold uppercase tracking-[0.15em] hover:bg-[#002088] disabled:opacity-60 transition-colors" data-testid="submit-task-btn">
              {saving ? 'Creating...' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
